import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';
import { MAX_STEPS, type Step } from '@personalizehq/editor';
import { ChevronLeft, ChevronRight, Plus, X } from 'lucide-react';

// Minimal multi-step (spec §1): a switcher over one artboard at a time. Rendering the steps SIDE BY
// SIDE as an arena is E4 — this is the strip it later becomes the tab bar of.

interface StepsStripProps {
  steps: Step[];
  focusedStepId: string;
  onFocus: (id: string) => void;
  onAdd: () => void;
  onRemove: (id: string) => void;
  onMove: (id: string, index: number) => void;
}

export function StepsStrip({
  steps,
  focusedStepId,
  onFocus,
  onAdd,
  onRemove,
  onMove,
}: StepsStripProps): React.JSX.Element {
  return (
    <div className="flex items-center gap-1">
      {steps.map((step, index) => {
        const active = step.id === focusedStepId;
        return (
          <div
            key={step.id}
            className={cn(
              'group flex items-center rounded-lg border text-[13px] transition-colors',
              active ? 'border-indigo-500 bg-secondary' : 'border-transparent hover:bg-secondary/60'
            )}
          >
            <button type="button" className="py-1 pl-2.5 pr-1.5" onClick={() => onFocus(step.id)}>
              {step.name}
            </button>

            {active ? (
              <span className="flex items-center pr-1 text-muted-foreground">
                <button
                  type="button"
                  aria-label={`Move ${step.name} earlier`}
                  disabled={index === 0}
                  className="p-0.5 disabled:opacity-30"
                  onClick={() => onMove(step.id, index - 1)}
                >
                  <ChevronLeft className="size-3" />
                </button>
                <button
                  type="button"
                  aria-label={`Move ${step.name} later`}
                  disabled={index === steps.length - 1}
                  className="p-0.5 disabled:opacity-30"
                  onClick={() => onMove(step.id, index + 1)}
                >
                  <ChevronRight className="size-3" />
                </button>
                <button
                  type="button"
                  aria-label={`Delete ${step.name}`}
                  // A doc must keep one step — `removeStep` throws otherwise, and a disabled button
                  // is a better answer than a caught exception.
                  disabled={steps.length === 1}
                  className="p-0.5 hover:text-destructive disabled:opacity-30"
                  onClick={() => onRemove(step.id)}
                >
                  <X className="size-3" />
                </button>
              </span>
            ) : null}
          </div>
        );
      })}

      <Button
        variant="ghost"
        size="icon"
        aria-label="Add step"
        className="size-7"
        disabled={steps.length >= MAX_STEPS}
        onClick={onAdd}
      >
        <Plus className="size-4" />
      </Button>
    </div>
  );
}
